import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Ballot, HybridContest, markedBallot } from './backend-types';
import { MteService } from '../Services/mte.service';
import { SessionStorageService } from '../Services/session-storage.service';
import { ApiService } from '../api/api.service';
import { writeInChoiceDescriptions } from './ballot.component';

@Injectable({
  providedIn: 'root'
})
export class BallotService {
  currentContest = 0;
  jsonWriteInDescriptions: writeInChoiceDescriptions[] = [];


  get contests(): HybridContest[] {
    return this.ballot?.contest_list ?? []
  }

  ballot: Ballot | undefined;
  ballotType: EventType = 'ELECTION';

  setBallot(ballot: Ballot) {
    this.ballot = ballot
    if(this.ballot?.eventType?.toLowerCase().includes('poll')) {
      this.ballotType = 'POLL';
    } else if(this.ballot?.eventType?.toLowerCase().includes('election')) {
      this.ballotType = 'ELECTION';
    } else {
      this.ballotType = 'PETITION';
    }
    this.jsonWriteInDescriptions = ballot.voteEventData.questions
    .flatMap(question => 
      question.statements.flatMap(statement =>
        statement.choices.filter(choice => choice.isWriteIn).map(choice => ({
          id: choice.choiceId,
          description: choice.description
        }))
      )
    );
    
    this.setupContestList()
    this.setCurrentContest(0)
  }

  setupContestList() {
    let ballot = this.ballot
    
    if (ballot) {
      const lst: HybridContest[] = [];
      for (let question of ballot.voteEventData.questions) {
        if (question.isRCV ?? false) {
          lst.push(new HybridContest(question, undefined))
        } else {
          for (let statement of question.statements) {           
            if (!(statement.declineToVoteContest ?? false)) {
              lst.push(new HybridContest(undefined, statement))
            }
          }
        }
      }
      ballot.contest_list = lst
    }
  }

  constructor(
    private readonly http: HttpClient,
    private readonly mteService: MteService,
    storer: SessionStorageService,
    private apiService: ApiService,
    @Inject(LOCALE_ID) readonly locale: string,
  ) { 
    this.ballot = storer.getBallot();
    this.setupContestList();
    this.currentContest = storer.getCurrentContest() ?? 0;
  }

  getCurrentContest(): number {
    return this.currentContest;
  }
  setCurrentContest(value: number): any {
    this.currentContest = value;
  }

  getBallot(): Ballot | undefined {
    return this.ballot;
  }

  getJsonDescrptions():writeInChoiceDescriptions[]{
    return this.jsonWriteInDescriptions
  }

  submit(ballot: Ballot, signature?: string, locale?: string): Observable<string> {
    const payload: SubmitPayload = { ballot: ballot }
    if (signature) {
      payload.signature = {
        content_type: "image/jpeg",
        transfer_encoding: ["x-base64"],
        data: signature
      }
    }
    if (locale) {
      payload.locale = locale
    }
    
    let response: Observable<HttpResponse<{confirmationId: string}>>
    if (this.mteService.useMte) {
      response = this.apiService.postWithHandshakeCheck<{confirmationId: string}>('/api/ballot', payload);
    }
    else {
      response = this.http.post<{ confirmationId: string }>(
        '/api/ballot',
        payload,
        { observe: 'response' as 'response', withCredentials: true }
      )
    }
    return response.pipe(
      map(r => {
        if(r.body) {
          return r.body.confirmationId;
        }
        else {
          throw new Error("No confirmation ID was in the response.");
        }
      }),
    );
  }

  submitMarkedBallot(ballot: Ballot, signature?: string): Observable<string> {
    const marked = markedBallot(ballot)
    return this.submit(marked, signature, this.locale)
  }

  isElectionLive(ballot: Ballot): boolean {
    let now = new Date();
    let eventStart = new Date(ballot.eventActivation);
    let eventEnd = new Date(ballot.eventExpiration);
    return (eventStart < now) && (now < eventEnd);
  }
}

interface SubmitPayload {
  ballot: Ballot;
  signature?: SignaturePayload;
  locale?: string;
}

interface SignaturePayload {
  content_type: string;
  transfer_encoding: string[];
  data?: string;
}

export type EventType = "ELECTION" | "PETITION" | "POLL";